import React, {useState} from 'react';
import {useTranslation, withTranslation} from "react-i18next";
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../reducers";
import {useSessionService} from "../../services/Session/sessionService";
import {getStoreConfig} from "../../services/Store/reducer";
import {getLocation} from "../../services/Session/reducer";
import Languages from "../Common/Languages";
import ErrorFormView from "../Common/ErrorFormView";
import ErrorFormHelper from "../../utils/ErrorFormHelper";
import ErrorFormAlertView from "../Common/ErrorFormAlertView";
import LoadingContainer from "../Common/LoadingContainer";
import QRCodeScannerComponent from "../Common/QRCodeScannerComponent";
import {ApiError, OrderType} from "../../services/Api/types";
import StoreUtils from "../../services/Store/StoreUtils";
import {SessionState} from "../../services/Session/types";
import IframeWidget from "../../utils/IframeWidget";
import PopupModal from "./PopupModal";
import {ReactComponent as RoomServiceIco} from "../../../images/icons/room-service.svg";
import {api} from "../../services/Api/api";
import ScannerUtils from "../../services/Scanner/ScannerUtils";

interface OwnProps {
    roomNumber: string,
    onSave: () => void,
    onHide: () => void,
    show: boolean,
    showLanguages?: boolean
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

function ChangeRoomServiceNumberModal(props: Props) {
    const [roomNumber, setRoomNumber] = useState(props.roomNumber ? props.roomNumber : "");
    const [errors, setErrors] = useState<ApiError[]>([]);
    const [loading, setLoading] = useState(false);
    const {t} = useTranslation();
    const sessionService = useSessionService();

    const onShow = () => {
        setRoomNumber(props.roomNumber ? props.roomNumber : "")
        setErrors([]);
    }
    let possibleEditNumber = props.config?.ordering.number_editable;

    const saveRoomNumber = async (number: string) => {
        setLoading(true);
        let sessionData = {
            ...props.session_state,
            order_type: "ROOM_SERVICE",
            room_number: number
        } as SessionState
        try {
            await sessionService.update(sessionData);
            props.onSave();
            IframeWidget.onHide(props.onHide)
            setLoading(false);
        } catch (err: any) {
            setErrors(err.errors);
            setLoading(false);
        }
    }

    const onScanQrToken = async (url: string) => {
        const token = ScannerUtils.extractToken(url);
        if (!token) {
            setErrors([{
                message: t("constraints.internal.wrong_qrcode_link")
            }])
            return;
        }
        try {
            setLoading(true);
            const response = await api.getQRCode(token);
            if (response && response.domain == props.config?.domain && response.number && response.type === OrderType.ROOM_SERVICE) {
                await saveRoomNumber(response.number);
                return;
            } else {
                setErrors([{
                    message: t("constraints.internal.wrong_qrcode_link")
                }])
            }
            setLoading(false)
        } catch (e: any) {
            setErrors([{
                message: t("constraints.internal.wrong_qrcode_link")
            }])
            setLoading(false)
        }
    }

    const onScan = async (data: string) => {
        if (!data) {
            setErrors([{
                message: t("constraints.internal.wrong_qrcode_link")
            }])
            return;
        }
        if (data.startsWith("http://") || data.startsWith("https://")) {
            if (data.includes("qr.goorder") || data.includes("demoqr.")) {
                await onScanQrToken(data);
                return;
            }
            try {
                const url = new URL(data);
                const pathSegments = url.pathname.split('/').filter(Boolean);
                const orderType = pathSegments[pathSegments.length - 1];
                if (!StoreUtils.isSameDomain(data, props.config?.domain) || !orderType || orderType !== "ROOM_SERVICE") {
                    setErrors([{
                        message: t("constraints.internal.wrong_qrcode_link")
                    }])
                    return;
                }
                const numberParam = url.searchParams.get("number");
                if (numberParam !== null) {
                    saveRoomNumber(numberParam);
                } else {
                    setErrors([{
                        message: t("constraints.internal.wrong_qrcode_link")
                    }])
                }
            } catch (error) {
                setErrors([{
                    message: t("constraints.internal.wrong_qrcode_link")
                }])
            }
        } else {
            setErrors([{
                message: t("constraints.internal.wrong_qrcode_link")
            }])
        }
    }

    return <PopupModal
        headerIcon={<RoomServiceIco/>}
        title={t("common.action.choose_room")}
        show={props.show}
        onHide={props.onHide}
        onShow={onShow}
        cancelButton
        bodyContent={<>
            <div>
                {props.showLanguages && (
                    <Languages size={"xl"}/>
                )}
                {!props.config?.ordering.scanner_disabled && (
                    <>
                        {errors.length > 0 ? (
                            <>
                                <ErrorFormAlertView errors={errors}/>
                                <button className={"btn btn-default btn-lg w-100 mb-4"}
                                        onClick={() => setErrors([])}>
                                    {t("common.action.scan_again")}
                                </button>
                            </>
                        ) : (
                            <>
                                {loading ? (
                                    <div>
                                        <LoadingContainer>
                                            {t("common.word.processing_in_progress")}
                                        </LoadingContainer>
                                    </div>
                                ) : (
                                    <>
                                        <p className={"text-center mb-1"}>
                                            {t("modules.cart.field.scan_room_qr_code.title")}
                                        </p>
                                        <QRCodeScannerComponent onScan={onScan}/>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}


                {(!possibleEditNumber && !roomNumber) ? (
                    <>
                        {props.config?.ordering.scanner_disabled && (
                            <div className={"alert alert-danger"}>
                                {t("constraints.internal.number_room_not_null")}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="form-group">
                            <input disabled={!possibleEditNumber} type="text" value={roomNumber}
                                   onChange={e => setRoomNumber(e.target.value)}
                                   className={"form-control" + ErrorFormHelper(errors, "number")}
                                   id="room_number"
                                   placeholder={t("modules.room_number_modal.field.room_number.title") + "*"}/>
                            <label
                                htmlFor="street">{t("modules.room_number_modal.field.room_number.title")}</label>
                            <ErrorFormView errors={errors} field="number"/>
                        </div>
                    </>
                )}
            </div>
        </>}
        primaryButton={possibleEditNumber ? (
            <button
                className={"btn-spinner btn-block btn-primary btn btn-submit" + (loading ? ' loading' : '')}
                onClick={() => saveRoomNumber(roomNumber)}>
                <span className="left spinner-border spinner-border-sm"/>
                {t("common.action.save")}
            </button>
        ) : undefined}
    />
}


const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    session_state: getLocation(state.session)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ChangeRoomServiceNumberModal));